import { buildCardField } from '@shared/data/cards/buildCardField';
import { Field } from '@shared/data/entity/fields/Field';
import { API } from '@shared/types/api';
import { User } from '@shared/types/user';

type EntityRefs = NonNullable<NonNullable<API.ListWorkflowEntitiesQuery['list']>['refs']>;

/**
 * What `buildCardField` is given when there is no saved card behind the field.
 *
 * Null rather than empty, because null is what the fields themselves check for: `PersonField`
 * treats `userIds` as the truth and refs as merely what happened to resolve, and its companion
 * hook fetches whatever did not (`useListUsersById`). Related-card lookup does the same through
 * `useWorkflowEntitiesByIds`. Nothing has been resolved at seeding time, so all four are null and
 * the fields hydrate themselves — subject to the viewer's own read permissions.
 */
const NO_REFS: EntityRefs = {
  personRefs: null,
  workflowEntityRefs: null,
  workflowTemplateRefs: null,
  recurringEntitiesScheduleRefs: null,
};

interface BuildInputFieldProps {
  component: API.AnyComponent;
  /** Raw field json, in the shape `Field.toFieldJson()` produces for this component's type. */
  data?: any;
  currentUser?: User;
}

/**
 * One input field built from raw json rather than from a card.
 *
 * The three places that seed a form from something other than a saved entity — the create form's
 * defaults, the builder preview, and a field patch — all reduce to this call, and all three had
 * their own copy of the null-refs literal. They still differ in *where* the json comes from, which
 * is why only the build is shared: `useDefaultFields` walks every component and falls back through
 * the view template to the component default, while a patch builds only the components it names and
 * has no fallback at all — an unnamed field must be left alone, not reset to its default.
 *
 * Returns null when there is nothing to build, which the callers treat as "no value here" rather
 * than as a failure.
 */
export function buildInputField({
  component,
  data,
  currentUser,
}: BuildInputFieldProps): Field | null {
  return buildCardField({
    entity: null,
    component,
    refs: NO_REFS,
    defaultData: data,
    currentUser,
  });
}
